import { ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PanelTop } from 'lucide-react';
import { Header } from '@/components/Header';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useChrome } from '../context/ChromeContext';
import { ArcheSidebar } from './ArcheSidebar';
import { SidebarProvider, useSidebar } from '../context/SidebarContext';
import { SearchResultsProvider } from '../context/SearchResultsContext';

/**
 * Display mode for the ArcheLayout:
 * - 'normal': Full header + sidebar + content (for authenticated users on non-fullscreen arches)
 * - 'public': Minimal header + content, no sidebar (for unauthenticated users on public arches)
 * - 'fullscreen': Minimal header (logo + theme toggle) + content, no sidebar (for full-screen arches)
 * - 'embed': No header at all, just content (for iframe embedding on external sites)
 */
export type ArcheDisplayMode = 'normal' | 'public' | 'fullscreen' | 'embed';

interface ArcheLayoutProps {
  children: ReactNode;
  /**
   * The display mode for the layout.
   * - 'normal': Full header + sidebar + content
   * - 'public': Minimal header + content (no sidebar)
   * - 'fullscreen': Minimal header (logo + theme toggle) + content (no sidebar)
   * - 'embed': No header at all, just content (for iframe embedding)
   */
  displayMode?: ArcheDisplayMode;
  /**
   * Suppress the default bottom-right focus-mode restore controls (Back-to-Fias
   * + Show-headers). An arche opts into this when it renders its own restore
   * affordance in a spot that fits its chrome (e.g. the email arche places them
   * above its account selector at the top-left). Without an in-arche
   * replacement, leave this false or the arche becomes un-restorable in focus
   * mode.
   */
  hideFloatingRestore?: boolean;
}

function ArcheLayoutInner({
  children,
  displayMode = 'normal',
  hideFloatingRestore = false,
}: ArcheLayoutProps) {
  const { id: archeId } = useParams<{ id: string }>();
  const { isCollapsed, isMobile, toggle, setCollapsed } = useSidebar();
  const { isHeadersHidden, showHeaders } = useChrome();

  // Embed mode: no header at all, just content (for iframe embedding)
  if (displayMode === 'embed') {
    return <div className="h-screen overflow-auto">{children}</div>;
  }

  // Full-screen mode: no header, no sidebar — content fills the entire viewport
  if (displayMode === 'fullscreen') {
    return <div className="h-screen overflow-auto">{children}</div>;
  }

  // Public mode (not logged in): minimal header, no sidebar
  if (displayMode === 'public') {
    return (
      <div className="flex flex-col h-screen">
        <Header minimal />
        <main className="flex-1 overflow-auto">{children}</main>
      </div>
    );
  }

  // Normal mode: full header + sidebar — unless this arche is in focus mode,
  // in which case the platform Header AND the suggested-arches sidebar are
  // dropped so the arche fills the viewport, with a single floating control to
  // restore them. State is per-arche (default visible) so other arches are
  // untouched.
  const headersHidden = isHeadersHidden(archeId);
  return (
    <div className="flex flex-col h-screen">
      {!headersHidden && <Header />}
      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Sidebar - only render on desktop and when chrome is shown */}
        {!isMobile && !headersHidden && (
          <aside
            className={`
              flex-shrink-0 border-r border-border bg-card overflow-hidden
              transition-all duration-300 ease-in-out
              ${isCollapsed ? 'w-0' : 'w-64'}
            `}
          >
            <div className="w-64 h-full flex flex-col">
              <ArcheSidebar currentArcheId={archeId || ''} onToggle={toggle} />
            </div>
          </aside>
        )}

        {/* Mobile Sheet Sidebar - only on mobile and when chrome is shown (in
            focus mode the toggle that opens it is gone, so don't leave it). */}
        {isMobile && !headersHidden && (
          <Sheet open={!isCollapsed} onOpenChange={(open) => setCollapsed(!open)}>
            <SheetContent side="left" className="w-[280px] p-0 [&>button]:hidden">
              <div className="h-full flex flex-col">
                <ArcheSidebar currentArcheId={archeId || ''} onToggle={toggle} />
              </div>
            </SheetContent>
          </Sheet>
        )}

        {/* Main content */}
        <main className="flex-1 overflow-auto">{children}</main>
      </div>

      {/* Focus-mode floating controls: leave the arche (Fias home) without
          un-hiding chrome, and the restore affordance (the only way back to the
          headers — no Esc in v1). Anchored BOTTOM-right: arche toolbars/headers
          live at the top, so a top-right control overlapped their content (e.g.
          the email reading-pane header). */}
      {headersHidden && archeId && !hideFloatingRestore && (
        <div className="fixed bottom-2 right-2 z-50 flex items-center gap-1">
          <Link
            to="/"
            className="rounded-md border border-border bg-card/90 p-1.5 shadow-sm backdrop-blur hover:opacity-80"
            title="Back to Fias home"
            aria-label="Back to Fias home"
          >
            <img src="/fias-logo.png" alt="Fias" className="h-4 w-auto dark:invert" />
          </Link>
          <button
            type="button"
            onClick={() => showHeaders(archeId)}
            className="rounded-md border border-border bg-card/90 p-1.5 text-muted-foreground shadow-sm backdrop-blur hover:text-primary"
            title="Show headers"
            aria-label="Show headers"
          >
            <PanelTop className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

export function ArcheLayout({
  children,
  displayMode = 'normal',
  hideFloatingRestore = false,
}: ArcheLayoutProps) {
  return (
    <SidebarProvider>
      <SearchResultsProvider>
        <ArcheLayoutInner displayMode={displayMode} hideFloatingRestore={hideFloatingRestore}>
          {children}
        </ArcheLayoutInner>
      </SearchResultsProvider>
    </SidebarProvider>
  );
}

export default ArcheLayout;
